import { supabase } from '../supabase';
import { isVatRate, LEGACY_VAT_RATE, ProductCategory, VatRate } from '../vat';

const mapCategory = (row: any): ProductCategory => ({
    id: row.id,
    name: row.name,
    vatRate: (isVatRate(row.vat_rate) ? Number(row.vat_rate) : LEGACY_VAT_RATE) as VatRate,
    nextVatRate: row.next_vat_rate != null && isVatRate(row.next_vat_rate) ? (Number(row.next_vat_rate) as VatRate) : undefined,
    nextVatFrom: row.next_vat_from || undefined,
    sortOrder: row.sort_order != null ? Number(row.sort_order) : undefined,
});

/** Produktkategorierna med momssats. Läses av butiken (publikt) och admin. */
export const getCategories = async (): Promise<ProductCategory[]> => {
    const { data, error } = await supabase.from('product_categories').select('*').order('name');
    if (error) {
        console.warn('Category fetch warning:', error.message);
        return [];
    }
    // Butikens ordning (sort_order), sedan namn. Sorteras här så att det fungerar även före migreringen.
    return (data || []).map(mapCategory).sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name, 'sv'));
};

/** Admin: skapar eller ändrar en kategori. Ändringen loggas i databasen (vat_rate_changes). */
export const saveCategory = async (category: Omit<ProductCategory, 'id'> & { id?: string }): Promise<ProductCategory> => {
    const payload = {
        name: category.name.trim(),
        vat_rate: category.vatRate,
        next_vat_rate: category.nextVatFrom ? category.nextVatRate ?? null : null,
        next_vat_from: category.nextVatFrom && category.nextVatRate !== undefined ? category.nextVatFrom : null,
        ...(category.sortOrder !== undefined ? { sort_order: Math.round(category.sortOrder) } : {}),
    };
    const query = category.id
        ? supabase.from('product_categories').update(payload).eq('id', category.id).select().single()
        : supabase.from('product_categories').insert([payload]).select().single();
    const { data, error } = await query;
    if (error) {
        if (error.code === '23505') throw new Error('Det finns redan en kategori med det namnet.');
        throw new Error(error.message);
    }
    return mapCategory(data);
};

/** Admin: tar bort en tom kategori. Databasen stoppar borttagning när produkter ligger i den. */
export const deleteCategory = async (id: string) => {
    const { error } = await supabase.from('product_categories').delete().eq('id', id);
    if (error) {
        if (error.code === '23503') throw new Error('Kategorin har produkter. Flytta dem först.');
        throw new Error(error.message);
    }
};

export interface VatRateChange {
    id: number;
    categoryId?: string;
    categoryName: string;
    oldVatRate?: number;
    newVatRate?: number;
    newNextVatRate?: number;
    newNextVatFrom?: string;
    changedAt: number;
}

/** Admin: loggen över momsändringar, nyast först. */
export const getVatRateChanges = async (categoryId?: string): Promise<VatRateChange[]> => {
    let query = supabase.from('vat_rate_changes').select('*').order('changed_at', { ascending: false }).limit(50);
    if (categoryId) query = query.eq('category_id', categoryId);
    const { data, error } = await query;
    if (error) {
        console.warn('Vat log fetch warning:', error.message);
        return [];
    }
    return (data || []).map((r: any) => ({
        id: r.id,
        categoryId: r.category_id || undefined,
        categoryName: r.category_name,
        oldVatRate: r.old_vat_rate ?? undefined,
        newVatRate: r.new_vat_rate ?? undefined,
        newNextVatRate: r.new_next_vat_rate ?? undefined,
        newNextVatFrom: r.new_next_vat_from || undefined,
        changedAt: new Date(r.changed_at).getTime(),
    }));
};
